import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Funnel } from "@b2bportal/core-types";
import type { IFlightFares } from "@overrides/types";
import { applyPackagePricingToFareData } from "@/modules/packages/utilities/packageFarePrice";
import {
  PackageFlightSelectors,
  PackageSearchSelectors,
} from "@/store/packages/selectors";
import { useGetCurrentFunnel } from "@/utilities/hooks";

/**
 * Returns the flight card's fare data with package pricing applied when in
 * the package funnel. Outside of packages the input is returned as-is.
 *
 * The collapsed flight card (the only price visible on mobile, where the fare
 * grid is hidden) derives its price from this fare data, so without this
 * substitution it would show the flight-only amount instead of the package
 * overage.
 */
export const usePackageFlightCardFareData = (
  fareData: IFlightFares
): IFlightFares => {
  const currentFunnel = useGetCurrentFunnel();

  const packageStateByFareId = useSelector(
    PackageFlightSelectors.getPackageStateByFareId
  );

  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );

  return useMemo(() => {
    if (currentFunnel !== Funnel.Package) {
      return fareData;
    }

    return applyPackagePricingToFareData(
      fareData,
      packageStateByFareId,
      packageType
    );
  }, [currentFunnel, fareData, packageStateByFareId, packageType]);
};
